import translate from "@/i18n/translate";
import { WmButton, WmFlyout } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import WidgetLoader from "../common/elements/WidgetLoader";
import { NoResults, Table, TableHeader, TableBody } from "../common/helper";
import {
  StudentAssessmentProgressBySection,
  Instructor
} from "../common/interfaces/Interface";
import React, { useReducer } from "react";
import { WmChart, WmChartSlice } from "@watermarkinsights/ripple-react";

export interface ViewCourseDetailsProps {
  CloseDrilldownView: () => void;
  courseName: string;
  courseCode: string;
  showTable: boolean;
  hierarchy: StudentAssessmentProgressBySection[];
  open: boolean;
}

interface NodeState {
  showAll: boolean;
}

const initialState: NodeState = {
  showAll: false
};

interface Action {
  type: string;
  showAll: boolean;
}
const defaultAction: Action = {
  type: "",
  showAll: false
};

const reducer = (state: NodeState, action: Action) => {
  switch (action.type) {
    case "toggleShowAll":
      return {
        ...state,
        showAll: !state.showAll
      };
    default:
      return state;
  }
};

function sortAlphabetically(instructorList: Instructor[]) {
  return instructorList.slice().sort((a, b) => {
    const firstNameA = a.firstName.toLowerCase();
    const firstNameB = b.firstName.toLowerCase();

    if (firstNameA < firstNameB) return -1;
    if (firstNameA > firstNameB) return 1;
    return 0;
  });
}

export default function ViewCourseDetails({
  CloseDrilldownView,
  courseName,
  courseCode,
  showTable,
  hierarchy,
  open
}: ViewCourseDetailsProps) {
  const widgetLabel = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.widgetLabel"
  });
  const trackStudentAssessmentProgress = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.trackStudentAssesmentProgress"
  });

  const courseHeading = `${courseCode} ${courseName}`;
  const flyoutWidth = "1316px";

  const getDrilldownColumns = () => {
    return [
      translate("app.drilldown.courseSection"),
      translate("app.drilldown.instructor"),
      translate("app.drilldown.progressStatus"),
      translate("app.drilldown.email")
    ];
  };

  const renderHierarchy = (node: StudentAssessmentProgressBySection) => {
    return (
      <React.Fragment key={`${node.uuid}-hierarchy`}>
        <Node node={node} />
      </React.Fragment>
    );
  };

  const renderHierarchyTable = () => {
    return (
      <Table>
        <TableHeader forCourseDetails={true} columns={getDrilldownColumns()} />
        <TableBody>
          {hierarchy.map((node: StudentAssessmentProgressBySection) => {
            return renderHierarchy(node);
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <WmFlyout
      id="drill-down-view"
      eyebrow={widgetLabel}
      subheading={trackStudentAssessmentProgress}
      heading={courseHeading}
      onWmFlyoutCloseTriggered={CloseDrilldownView}
      open={open}
      flyoutWidth={flyoutWidth}
      className="sll-flyout-container"
    >
      <section className="body course-details-body">
        <div className="flex-row -justified -verticallycentered">
          <div>
            <div className="title -title">
              {courseCode} {courseName}
            </div>
            <div className="table-header">
              {translate(
                "app.programCurriculumEngagement.progressBySection"
              )}
            </div>
            {hierarchy && hierarchy.length > 0 && (
              <div className="legend-wrapper">
                <div className="legend" aria-hidden="true">
                  <div className="legend-item">
                    <div className="legend-color legend1 -legend-wrap"></div>
                    <div className="legend-text">
                      {translate(
                        "app.assessmentProgressChart.awaitingSubmission"
                      )}
                    </div>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color legend2 -legend-wrap"></div>
                    <div className="legend-text">
                      {translate(
                        "app.assessmentProgressChart.awaitingAssessment"
                      )}
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color legend3"></div>
                    <div className="legend-text">
                      {translate("app.done")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {showTable && hierarchy ? (
          <div className="org-table">
            {!Object.keys(hierarchy).length ? (
              <NoResults />
            ) : (
              renderHierarchyTable()
            )}
          </div>
        ) : (
          <div className="loader">
            <WidgetLoader
              loaderContainerClasses="loader"
              loaderClasses="content"
            />
          </div>
        )}
      </section>
    </WmFlyout>
  );
}

interface NodeProps {
  node: StudentAssessmentProgressBySection;
}
const Node = ({ node }: NodeProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const instructorList = node.instructors;

  const sortedInstructorList = sortAlphabetically(instructorList);

  const instructors = sortedInstructorList.map((instructor: Instructor) => {
    return <div>{`${instructor["firstName"]} ${instructor["lastName"]}`}</div>;
  });

  const leadEmail = node.instructors.map((instructor: Instructor) => {
    return `${instructor["email"]}`;
  });
  const emailInstructorTooltip = useIntl().formatMessage(
    {
      id: "app.programCurriculumEngagement.emailInstructorTooltip"
    },
    { sectionNumber: node.sectionNumber, sectionId: node.sectionId }
  );

  const toggleButton = () => {
    dispatch({ ...defaultAction, type: "toggleShowAll" });
  };

  return (
    <tr className="datarow" key={`${node["uuid"]}-datarow`}>
      <td className={`datacell`}>
        <div className="course-section">{`${node["sectionNumber"]} - ${node["sectionId"]}`}</div>
      </td>
      <td className={`datacell`}>
        <div
          className={
            state.showAll ? "instructors show-less" : "instructors show-more"
          }
        >
          {instructors}
        </div>
        {node.instructors.length > 3 && (
          <WmButton
            onClick={toggleButton}
            button-type="textonly"
            className="show-more-btn"
          >
            {state.showAll
              ? translate("app.programCurriculumEngagement.showLess")
              : translate("app.programCurriculumEngagement.showMore")}
          </WmButton>
        )}
      </td>
      <td className="datacell">
        {node.studentAssessments.awaitingSubmission > 0 ||
          node.studentAssessments.awaitingAssessment > 0 ||
          node.studentAssessments.done > 0 ? (
          <Graph node={node} />
        ) : (
          <div>
            {translate(
              "app.programCurriculumEngagement.NoAssesmentsInProgress"
            )}
          </div>
        )}
      </td>
      <td className="datacell">
        {node.instructors.length > 0 && (
          <a className="email_link" tabIndex={-1} href={`mailto: ${leadEmail}`}>
            <WmButton
              button-type="icononly"
              tooltip={emailInstructorTooltip}
              icon="f1f0"
              icon-size="18px"
            ></WmButton>
          </a>
        )}
      </td>
    </tr>
  );
};

const Graph = ({ node }: NodeProps) => {
  const awaitingSubmission = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingSubmission"
  });
  const awaitingAssessment = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingAssessment"
  });
  const done = useIntl().formatMessage({
    id: "app.done"
  });
  const awaitingSubmissionTooltip = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.awaitingSubmission"
  });
  const awaitingAssesmentTooltip = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.awaitingAssessment"
  });
  const doneTooltip = useIntl().formatMessage({
    id: "app.programCurriculumEngagement.done"
  });

  const totalStudents =
    node.studentAssessments.awaitingSubmission +
    node.studentAssessments.awaitingAssessment +
    node.studentAssessments.done;
  return (
    <div key={String(node.uuid)}>
      <div className="chart-space">
        <WmChart
          chart-type="bar4"
          value-format="amount"
          show-legend="false"
          label=" "
        >
          <WmChartSlice
            legend={awaitingSubmission}
            amount={String(node.studentAssessments.awaitingSubmission)}
            popover-text={awaitingSubmissionTooltip}
            popover-title={`${node.studentAssessments.awaitingSubmission}/${totalStudents}`}
          />
          <WmChartSlice
            legend={awaitingAssessment}
            amount={String(node.studentAssessments.awaitingAssessment)}
            popover-text={awaitingAssesmentTooltip}
            popover-title={`${node.studentAssessments.awaitingAssessment}/${totalStudents}`}
          />
          <WmChartSlice
            legend={done}
            amount={String(node.studentAssessments.done)}
            popover-text={doneTooltip}
            popover-title={`${node.studentAssessments.done}/${totalStudents}`}
          />
        </WmChart>
      </div>
    </div>
  );
};
